import type { Publisher, PublisherProperties } from "openvidu-browser-v2compatibility";

export type InsuredPublisherProperties = PublisherProperties & {
  videoSimulcast?: boolean;
};

interface InsuredHdProfile {
  name: string;
  width: number;
  height: number;
}

interface InsuredHdAttempt {
  label: string;
  constraints: MediaTrackConstraints;
}

export interface PublisherVideoSettingsSnapshot {
  width?: number;
  height?: number;
  frameRate?: number;
  deviceId?: string;
  facingMode?: string;
}

export interface InsuredHdPreflightResult {
  track?: MediaStreamTrack;
  requestedProfile?: string;
  settings?: PublisherVideoSettingsSnapshot;
  attempts: string[];
  fallbackReason?: string;
  failures?: string[];
}

export const INSURED_PUBLISHER_TARGET_RESOLUTION = "960x720";
export const INSURED_PUBLISHER_TARGET_FRAME_RATE = 20;
export const INSURED_PUBLISHER_INSERT_MODE = "APPEND";
const INSURED_SENDER_DEGRADATION_PREFERENCE = "balanced";
const INSURED_TRACK_CONTENT_HINT = "detail";
const INSURED_SENDER_MAX_BITRATE = 1_500_000;
const INSURED_SENDER_LOW_RES_MAX_BITRATE = 1_200_000;
const INSURED_SENDER_RETRY_ATTEMPTS = 8;
const INSURED_SENDER_RETRY_INTERVAL_MS = 350;
const INSURED_HD_PREFLIGHT_PROFILES: InsuredHdProfile[] = [
  { name: "960x720", width: 960, height: 720 },
  { name: "720x960", width: 720, height: 960 },
  { name: "1280x720", width: 1280, height: 720 },
  { name: "720x1280", width: 720, height: 1280 },
  { name: "1024x768", width: 1024, height: 768 },
  { name: "768x1024", width: 768, height: 1024 },
];

export function createInsuredPublisherOptions(
  overrides: Partial<InsuredPublisherProperties> = {}
): InsuredPublisherProperties {
  return {
    resolution: INSURED_PUBLISHER_TARGET_RESOLUTION,
    insertMode: INSURED_PUBLISHER_INSERT_MODE,
    audioSource: undefined,
    videoSource: undefined,
    publishAudio: true,
    publishVideo: true,
    frameRate: INSURED_PUBLISHER_TARGET_FRAME_RATE,
    mirror: false,
    videoSimulcast: false,
    ...overrides,
  };
}

export async function tryAcquireInsuredHdVideoTrack(
  deviceId?: string
): Promise<InsuredHdPreflightResult> {
  const attempts: string[] = [];
  const failures: string[] = [];
  const mediaDevices = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;

  if (!mediaDevices?.getUserMedia) {
    return {
      attempts,
      fallbackReason: "media_devices_unavailable",
      failures,
    };
  }

  const preflightAttempts = createHdPreflightAttempts(deviceId);

  for (const attempt of preflightAttempts) {
    attempts.push(attempt.label);

    try {
      const mediaStream = await mediaDevices.getUserMedia({
        audio: false,
        video: attempt.constraints,
      });

      const track = mediaStream.getVideoTracks()?.[0];
      if (!track) {
        stopMediaStreamTracks(mediaStream);
        continue;
      }

      const settings = readVideoTrackSettings(track);
      if (isAtLeast720VideoSettings(settings)) {
        stopSiblingTracks(mediaStream, track);
        return {
          track,
          settings,
          attempts,
          requestedProfile: attempt.label,
          failures,
        };
      }

      stopMediaStreamTracks(mediaStream);
    } catch (error) {
      failures.push(formatPreflightFailure(attempt.label, error));
      continue;
    }
  }

  return {
    attempts,
    fallbackReason: "preflight_hd_not_available",
    failures,
  };
}

export function readPublisherVideoSettings(
  publisher?: Publisher | null
): PublisherVideoSettingsSnapshot | undefined {
  try {
    const mediaStream = publisher?.stream?.getMediaStream?.();
    const track = mediaStream?.getVideoTracks?.()?.[0];
    return readVideoTrackSettings(track);
  } catch (error) {
    console.warn("[publisherConfig.readPublisherVideoSettings]", error);
    return undefined;
  }
}

export async function applyInsuredSenderHints(publisher?: Publisher | null): Promise<boolean> {
  try {
    const peerConnection = publisher?.stream?.getRTCPeerConnection?.();
    const senders = peerConnection?.getSenders?.();
    const videoSender = senders?.find((sender) => sender?.track?.kind === "video");

    if (!videoSender) {
      return false;
    }

    let hasChanges = false;
    const videoTrack = videoSender.track;

    if (videoTrack && "contentHint" in videoTrack && videoTrack.contentHint !== INSURED_TRACK_CONTENT_HINT) {
      videoTrack.contentHint = INSURED_TRACK_CONTENT_HINT;
      hasChanges = true;
    }

    if (typeof videoSender.getParameters !== "function" || typeof videoSender.setParameters !== "function") {
      return hasChanges;
    }

    const currentParameters = videoSender.getParameters() || {};
    const nextParameters = { ...currentParameters };
    const targetMaxBitrate = resolveSenderMaxBitrate(videoTrack);

    if (nextParameters.degradationPreference !== INSURED_SENDER_DEGRADATION_PREFERENCE) {
      nextParameters.degradationPreference = INSURED_SENDER_DEGRADATION_PREFERENCE;
      hasChanges = true;
    }

    if (Array.isArray(nextParameters.encodings) && nextParameters.encodings.length > 0) {
      const nextEncodings = nextParameters.encodings.map((encoding: any) => ({ ...encoding }));

      for (const encoding of nextEncodings) {
        // Degradação adaptativa: NÃO travamos scaleResolutionDownBy. Deixamos o encoder
        // ceder resolução sob congestão (frame limpo menor > HD corrompido).
        if (encoding.maxBitrate !== targetMaxBitrate) {
          encoding.maxBitrate = targetMaxBitrate;
          hasChanges = true;
        }
      }

      nextParameters.encodings = nextEncodings;
    }

    if (hasChanges) {
      await videoSender.setParameters(nextParameters);
    }

    return true;
  } catch (error) {
    console.warn("[publisherConfig.applyInsuredSenderHints]", error);
    return false;
  }
}

export async function applyInsuredSenderHintsWithRetry(
  publisher?: Publisher | null,
  attempts = INSURED_SENDER_RETRY_ATTEMPTS,
  intervalMs = INSURED_SENDER_RETRY_INTERVAL_MS
): Promise<boolean> {
  const maxAttempts = Math.max(1, attempts);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const applied = await applyInsuredSenderHints(publisher);
    if (applied) {
      return true;
    }

    if (attempt < maxAttempts - 1) {
      await sleep(intervalMs);
    }
  }

  return false;
}

export function formatPublisherVideoSettingsMessage(
  context: string,
  settings: PublisherVideoSettingsSnapshot
): string {
  const resolution = settings.width && settings.height ? `${settings.width}x${settings.height}` : "unknown";
  const frameRate = settings.frameRate ? ` @ ${settings.frameRate}fps` : "";
  const facingMode = settings.facingMode ? ` facingMode=${settings.facingMode}` : "";
  const deviceId = settings.deviceId ? ` device=${settings.deviceId}` : "";

  return `Segurado: ${context}. Video efetivo ${resolution}${frameRate}${facingMode}${deviceId}`;
}

export function formatInsuredHdPreflightMessage(context: string, result: InsuredHdPreflightResult): string {
  const attempts = result.attempts.length > 0 ? result.attempts.join(" -> ") : "none";
  const selected = result.requestedProfile ?? "none";
  const status = result.track ? "locked" : "fallback";
  const resolution =
    result.settings?.width && result.settings?.height ? `${result.settings.width}x${result.settings.height}` : "unknown";
  const frameRate = result.settings?.frameRate ? ` @ ${result.settings.frameRate}fps` : "";
  const reason = result.fallbackReason ? ` reason=${result.fallbackReason}` : "";
  const failures =
    result.failures && result.failures.length > 0 ? ` failures=${result.failures.join(" | ")}` : "";

  return `Segurado: ${context}. Preflight HD status=${status} requested=${selected} attempts=${attempts} effective=${resolution}${frameRate}${reason}${failures}`;
}

export function isAtLeast720VideoSettings(settings?: PublisherVideoSettingsSnapshot) {
  if (!settings?.width || !settings?.height) {
    return false;
  }

  const shortestSide = Math.min(settings.width, settings.height);
  const longestSide = Math.max(settings.width, settings.height);

  return shortestSide >= 720 && longestSide >= 960;
}

export function readVideoTrackSettings(track?: MediaStreamTrack | null): PublisherVideoSettingsSnapshot | undefined {
  if (!track?.getSettings) {
    return undefined;
  }

  const settings = track.getSettings();
  return {
    width: toPositiveNumber(settings.width),
    height: toPositiveNumber(settings.height),
    frameRate: toPositiveNumber(settings.frameRate),
    deviceId: typeof settings.deviceId === "string" && settings.deviceId.length > 0 ? settings.deviceId : undefined,
    facingMode: typeof settings.facingMode === "string" && settings.facingMode.length > 0 ? settings.facingMode : undefined,
  };
}

function toPositiveNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;
}

function resolveSenderMaxBitrate(track?: MediaStreamTrack | null) {
  const settings = readVideoTrackSettings(track);

  if (settings && !isAtLeast720VideoSettings(settings)) {
    return INSURED_SENDER_LOW_RES_MAX_BITRATE;
  }

  return INSURED_SENDER_MAX_BITRATE;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildHdPreflightConstraints(profile: InsuredHdProfile, deviceId?: string): MediaTrackConstraints {
  const targetAspectRatio = profile.width / profile.height;
  const constraints: MediaTrackConstraints = {
    width: { ideal: profile.width },
    height: { ideal: profile.height },
    aspectRatio: { ideal: targetAspectRatio },
    frameRate: { ideal: INSURED_PUBLISHER_TARGET_FRAME_RATE },
  };

  if (deviceId) {
    constraints.deviceId = { exact: deviceId };
  } else {
    constraints.facingMode = { ideal: "environment" };
  }

  return constraints;
}

function stopMediaStreamTracks(mediaStream?: MediaStream | null) {
  mediaStream?.getTracks?.().forEach((track) => track.stop());
}

function stopSiblingTracks(mediaStream: MediaStream, selectedTrack: MediaStreamTrack) {
  mediaStream.getTracks().forEach((track) => {
    if (track !== selectedTrack) {
      track.stop();
    }
  });
}

function createHdPreflightAttempts(deviceId?: string): InsuredHdAttempt[] {
  const attempts: InsuredHdAttempt[] = [];

  if (deviceId) {
    for (const profile of INSURED_HD_PREFLIGHT_PROFILES) {
      attempts.push({
        label: `${profile.name}@deviceId`,
        constraints: buildHdPreflightConstraints(profile, deviceId),
      });
    }
  }

  for (const profile of INSURED_HD_PREFLIGHT_PROFILES) {
    attempts.push({
      label: `${profile.name}@environment`,
      constraints: buildHdPreflightConstraints(profile),
    });
  }

  return attempts;
}

function formatPreflightFailure(attemptLabel: string, error: unknown) {
  const errorName =
    error && typeof error === "object" && "name" in error && typeof (error as any).name === "string"
      ? (error as any).name
      : "UnknownError";
  const failedConstraint =
    error && typeof error === "object" && "constraint" in error && typeof (error as any).constraint === "string"
      ? (error as any).constraint
      : undefined;

  return failedConstraint ? `${attemptLabel}:${errorName}(${failedConstraint})` : `${attemptLabel}:${errorName}`;
}
